import { useCallback, useEffect, useRef } from 'react';
import {
  useQuizResultActions,
  useQuizGameState,
  useQuizMetaContext,
  useQuizResults,
  useHasQuizResults,
} from '@mentimeter/quiz';
import type { QuizPlayersScoreData } from '@mentimeter/core-hooks';
import { GameStateEnums, type InitialGameStates } from '@mentimeter/quiz-types';
import type { QuizOpenStaticContent } from '@core-api/presentation/types/response';
import {
  usePresentationContext,
  usePresentationSlideContext,
} from '../../legacy-context/presentationContext';
import { GameStatesSwitcher } from './GameStateSwitcher';
import { useQuizOpenPreviewResultsLegacy } from './hooks/use-quiz-open-preview-results-legacy';

// Needs to be a type instead of interface as the interface can't satisfy the generic used with useSlideState hook
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export type QuizSlideState = {
  started?: boolean;
  starting?: boolean;
};

export const PresentersPresentationDataHandler = ({
  initialGameState,
  activeSession,
}: {
  initialGameState: InitialGameStates;
  activeSession: number;
}) => {
  const { useSlideState, useConfig, isPreviewResultsEnabled } =
    usePresentationContext();
  const { readonly } = useConfig();
  const slide = usePresentationSlideContext();

  const staticContent = slide.staticContent as QuizOpenStaticContent;
  const interactiveContent = slide.interactiveContents[0]!;

  const { totalCount, currentIndex } = useQuizMetaContext();
  const { data = [] } = useQuizResults<QuizPlayersScoreData>();
  const quizHasResults = useHasQuizResults();
  const { markAnswer } = useQuizResultActions(slide.slideAdminKey);
  const {
    gameState,
    timeOffset = 0,
    connectedPlayers,
    respondents,
    startQuiz: _startQuiz,
    createQuiz,
    everyoneHasVoted,
    connected,
    connectedWithDelay,
  } = useQuizGameState();
  const [{ started, starting }, setSlideState] =
    useSlideState<QuizSlideState>();

  const startQuiz = useCallback(() => {
    _startQuiz({ countdownToVote: interactiveContent.countdown ?? 0 });
    setSlideState({ started: true, starting: false });
  }, [interactiveContent.countdown, setSlideState, _startQuiz]);

  /**
   * If the presenter were to navigate to the quiz slide again (or reset results), we need to either:
   * - Set `started` to `true` if there are results, so that the presenter can navigate to the next slide
   *  without having to start the quiz again.
   * - Reset `started` to `false` if there are no results, so that the presenter can start the quiz
   */
  useEffect(() => {
    if (quizHasResults) {
      setSlideState({ started: true, starting: false });
    } else {
      setSlideState({ started: false, starting: false });
    }
  }, [setSlideState, quizHasResults]);

  useEffect(() => {
    if (starting && !started) {
      startQuiz();
    }
  }, [startQuiz, started, starting]);

  const prevActiveSession = useRef(activeSession);
  const createdRef = useRef(false);
  useEffect(() => {
    const sessionChanged = prevActiveSession.current !== activeSession;
    if ((!gameState && !createdRef.current) || sessionChanged) {
      createQuiz(sessionChanged ? GameStateEnums.LOBBY : initialGameState);
      createdRef.current = true;
      prevActiveSession.current = activeSession;
    }
  }, [createQuiz, initialGameState, activeSession, gameState]);

  const onReload = useCallback(() => {
    if (connected) {
      createQuiz(initialGameState);
    } else {
      window.location.reload();
    }
  }, [connected, createQuiz, initialGameState]);

  const firstCorrectAnswer = interactiveContent.choices[0]?.title;

  const previewResults = useQuizOpenPreviewResultsLegacy({
    isPreviewResultsEnabled,
    correctAnswer: firstCorrectAnswer,
  });

  const resultsToShow = isPreviewResultsEnabled ? previewResults : data;

  const shouldShowLegacyImage =
    staticContent.image.imageLayoutPosition.start === 0 &&
    staticContent.image.imageLayoutPosition.end === 0;

  return (
    <GameStatesSwitcher
      shouldShowLegacyImage={shouldShowLegacyImage}
      visibleCorrectAnswer={firstCorrectAnswer ?? ''}
      imageUrl={
        slide.images?.[0]?.presets?.original?.url ??
        slide.images?.[0]?.presets?.large?.url
      }
      timeBasedScoring={interactiveContent.scoring === 'time_based'}
      title={slide.title}
      styledTitle={staticContent.styledTitle as Record<string, unknown>}
      label={staticContent.label}
      description={interactiveContent.description}
      textSizeOffset={staticContent.textSizeOffset}
      isReadonly={readonly}
      onMarkAnswer={markAnswer}
      results={resultsToShow}
      isPreviewResults={isPreviewResultsEnabled}
      gameState={gameState}
      timeOffset={timeOffset}
      connectedPlayers={connectedPlayers}
      respondents={respondents}
      onReload={onReload}
      everyoneHasVoted={everyoneHasVoted}
      connectedWithDelay={connectedWithDelay}
      totalQuizQuestions={totalCount}
      currentQuizQuestionIndex={currentIndex}
    />
  );
};
